import { ref, computed } from "vue";
import { useToast } from "primevue/usetoast";
import { useQueryClient } from "@tanstack/vue-query";
import {
  usePostCreateRecipe,
  useGetFetchRecipe,
  getGetFetchRecipeQueryKey,
  usePostFetchAllRecipes,
  usePostCreateAction,
  usePutUpdateAction,
  usePostUpdateActionStatus,
  usePostAddActionToRecipe,
  usePostFetchAllActions,
  usePostAddArticleToAction,
  usePostFetchAllArticles,
  getFetchAction,
} from "src/api/queries/order-portal-bff";
import {
  useDeleteAbstractAction,
  useRemoveActionFromRecipe,
  useRemoveArticleFromAbstractAction,
} from "src/api/stubs/recipe-stubs";
import type { FetchRecipeResponseLite } from "src/api/models/fetchRecipeResponseLite";
import type { FetchRecipeResponse } from "src/api/models/fetchRecipeResponse";
import type { FetchActionResponseLite } from "src/api/models/fetchActionResponseLite";
import type { CreateRecipeDTO } from "src/api/models/createRecipeDTO";
import type { CreateActionDTO } from "src/api/models/createActionDTO";
import type { UpdateActionDTO } from "src/api/models/updateActionDTO";
import type { AbstractActionFiltersDTO } from "src/api/models/abstractActionFiltersDTO";
import type { ArticleFiltersDTO } from "src/api/models/articleFiltersDTO";
import { UpdateActionStatusDTOStatus } from "src/api/models/updateActionStatusDTOStatus";

export type ViewMode = "recipes" | "actions";

export function useRecipeManagement() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const viewMode = ref<ViewMode>("recipes");

  // Recipe list state
  const recipeList = ref<FetchRecipeResponseLite[]>([]);
  const recipeListPage = ref(1);
  const recipeListTotalPages = ref(1);
  const recipeListLoading = ref(false);
  let recipeLoadRequestId = 0;

  // Action list state
  const actionList = ref<FetchActionResponseLite[]>([]);
  const actionListPage = ref(1);
  const actionListTotalPages = ref(1);
  const actionListLoading = ref(false);
  let actionLoadRequestId = 0;

  // Shared state
  const searchQuery = ref("");
  const typeFilter = ref<string | null>(null);
  const statusFilter = ref<string | null>(null);
  const operationInProgress = ref(false);
  const expandedRecipeId = ref<string | null>(null);

  // Mutation hooks — real APIs
  const { mutateAsync: fetchAllRecipes } = usePostFetchAllRecipes();
  const { mutateAsync: fetchAllActions } = usePostFetchAllActions();
  const { mutateAsync: createRecipe } = usePostCreateRecipe();
  const { mutateAsync: createAction } = usePostCreateAction();
  const { mutateAsync: updateAbstractAction } = usePutUpdateAction();
  const { mutateAsync: updateAbstractActionStatus } = usePostUpdateActionStatus();
  const { mutateAsync: addActionToRecipe } = usePostAddActionToRecipe();
  const { mutateAsync: addArticleToAbstractAction } = usePostAddArticleToAction();
  const { mutateAsync: fetchAllArticles } = usePostFetchAllArticles();
  // Stubs
  const { mutateAsync: removeActionFromRecipeMutate } = useRemoveActionFromRecipe();
  const { mutateAsync: deleteAbstractActionMutate } = useDeleteAbstractAction();
  const { mutateAsync: removeArticleMutate } = useRemoveArticleFromAbstractAction();

  // Query for expanded recipe detail
  const expandedRecipeQuery = useGetFetchRecipe(
    computed(() => expandedRecipeId.value ?? ""),
    {
      query: {
        enabled: computed(() => !!expandedRecipeId.value),
      },
    },
  );

  const expandedRecipe = computed<FetchRecipeResponse | null>(
    () => expandedRecipeQuery.data?.value ?? null,
  );
  const expandedRecipeQueryLoading = computed(() => expandedRecipeQuery.isLoading?.value ?? false);

  function buildFilters(): AbstractActionFiltersDTO {
    const filters: AbstractActionFiltersDTO = {};
    if (searchQuery.value.trim()) filters.name = searchQuery.value.trim();
    if (typeFilter.value) filters.type = typeFilter.value as AbstractActionFiltersDTO["type"];
    return filters;
  }

  async function loadRecipes(page = 1) {
    const thisRequest = ++recipeLoadRequestId;
    recipeListLoading.value = true;
    try {
      const response = await fetchAllRecipes({
        data: buildFilters(),
        params: { page, limit: 25 },
      });
      if (thisRequest !== recipeLoadRequestId) return;
      recipeList.value = response.data ?? [];
      recipeListPage.value = response.page ?? 1;
      recipeListTotalPages.value = response.total_pages ?? 1;
    } catch {
      if (thisRequest !== recipeLoadRequestId) return;
      toast.add({ severity: "error", summary: "Error", detail: "Failed to load recipes", life: 3000 });
    } finally {
      if (thisRequest === recipeLoadRequestId) recipeListLoading.value = false;
    }
  }

  async function loadActions(page = 1) {
    const thisRequest = ++actionLoadRequestId;
    actionListLoading.value = true;
    try {
      const response = await fetchAllActions({
        data: buildFilters(),
        params: { page, limit: 25 },
      });
      if (thisRequest !== actionLoadRequestId) return;
      actionList.value = response.data ?? [];
      actionListPage.value = response.page ?? 1;
      actionListTotalPages.value = response.total_pages ?? 1;
    } catch {
      if (thisRequest !== actionLoadRequestId) return;
      toast.add({ severity: "error", summary: "Error", detail: "Failed to load actions", life: 3000 });
    } finally {
      if (thisRequest === actionLoadRequestId) actionListLoading.value = false;
    }
  }

  function loadCurrentView(page = 1) {
    if (viewMode.value === "recipes") loadRecipes(page);
    else loadActions(page);
  }

  function expandRecipe(id: string | null) {
    expandedRecipeId.value = id;
  }

  async function refreshExpandedRecipe() {
    if (expandedRecipeId.value) {
      queryClient.invalidateQueries({ queryKey: getGetFetchRecipeQueryKey(expandedRecipeId.value) });
    }
  }

  async function handleCreateRecipe(data: CreateRecipeDTO) {
    operationInProgress.value = true;
    try {
      await createRecipe({ data });
      toast.add({ severity: "success", summary: "Success", detail: "Recipe created", life: 3000 });
      await loadRecipes(recipeListPage.value);
    } finally {
      operationInProgress.value = false;
    }
  }

  async function handleCreateAction(data: CreateActionDTO, linkToRecipeId?: string) {
    operationInProgress.value = true;
    try {
      const createdId = await createAction({ data });
      if (linkToRecipeId && createdId) {
        try {
          await addActionToRecipe({ id: linkToRecipeId, data: { actions: [createdId] } });
        } catch {
          toast.add({
            severity: "warn",
            summary: "Partial success",
            detail: "Action created but failed to link to recipe. Link it manually.",
            life: 5000,
          });
          await refreshExpandedRecipe();
          return;
        }
      }
      toast.add({ severity: "success", summary: "Success", detail: "Action created", life: 3000 });
      if (linkToRecipeId) await refreshExpandedRecipe();
      if (viewMode.value === "actions") await loadActions(actionListPage.value);
    } finally {
      operationInProgress.value = false;
    }
  }

  async function handleUpdateAbstractAction(id: string, data: UpdateActionDTO) {
    operationInProgress.value = true;
    try {
      await updateAbstractAction({ id, data });
      toast.add({ severity: "success", summary: "Success", detail: "Updated", life: 3000 });
      await refreshExpandedRecipe();
      await loadCurrentView();
    } finally {
      operationInProgress.value = false;
    }
  }

  async function handleToggleStatus(id: string, currentStatus: string) {
    operationInProgress.value = true;
    try {
      const newStatus = currentStatus === "active"
        ? UpdateActionStatusDTOStatus.inactive
        : UpdateActionStatusDTOStatus.active;
      await updateAbstractActionStatus({ id, data: { status: newStatus } });
      toast.add({
        severity: "success",
        summary: "Success",
        detail: `${newStatus === "active" ? "Activated" : "Deactivated"}`,
        life: 3000,
      });
      await refreshExpandedRecipe();
      await loadCurrentView();
    } finally {
      operationInProgress.value = false;
    }
  }

  async function handleDelete(id: string) {
    operationInProgress.value = true;
    try {
      await deleteAbstractActionMutate({ id });
      toast.add({ severity: "success", summary: "Success", detail: "Deleted", life: 3000 });
      if (expandedRecipeId.value === id) expandedRecipeId.value = null;
      await loadCurrentView();
    } finally {
      operationInProgress.value = false;
    }
  }

  async function handleLinkAction(recipeId: string, actionId: string) {
    operationInProgress.value = true;
    try {
      await addActionToRecipe({ id: recipeId, data: { actions: [actionId] } });
      toast.add({ severity: "success", summary: "Success", detail: "Action linked", life: 3000 });
      await refreshExpandedRecipe();
    } finally {
      operationInProgress.value = false;
    }
  }

  async function handleRemoveAction(recipeId: string, actionId: string) {
    operationInProgress.value = true;
    try {
      await removeActionFromRecipeMutate({ id: recipeId, data: { actions: [actionId] } });
      toast.add({ severity: "success", summary: "Success", detail: "Action removed from recipe", life: 3000 });
      await refreshExpandedRecipe();
    } finally {
      operationInProgress.value = false;
    }
  }

  async function handleAddArticle(abstractActionId: string, articleId: string) {
    operationInProgress.value = true;
    try {
      await addArticleToAbstractAction({ id: abstractActionId, data: { articles: [articleId] } });
      toast.add({ severity: "success", summary: "Success", detail: "Article linked", life: 3000 });
      await refreshExpandedRecipe();
    } finally {
      operationInProgress.value = false;
    }
  }

  async function handleRemoveArticle(abstractActionId: string, articleId: string) {
    operationInProgress.value = true;
    try {
      await removeArticleMutate({ id: abstractActionId, data: { articles: [articleId] } });
      toast.add({ severity: "success", summary: "Success", detail: "Article removed", life: 3000 });
      await refreshExpandedRecipe();
    } finally {
      operationInProgress.value = false;
    }
  }

  async function searchActionsForLink(query: string) {
    const filters: AbstractActionFiltersDTO = {};
    if (query.trim()) filters.name = query.trim();
    const response = await fetchAllActions({ data: filters, params: { page: 1, limit: 20 } });
    return response.data ?? [];
  }

  async function searchArticles(query: string) {
    const filters: ArticleFiltersDTO = {};
    if (query.trim()) filters.name = query.trim();
    const response = await fetchAllArticles({ data: filters, params: { page: 1, limit: 20 } });
    return response.data ?? [];
  }

  async function fetchActionDetail(id: string) {
    return getFetchAction(id);
  }

  return {
    viewMode,
    searchQuery,
    typeFilter,
    statusFilter,
    operationInProgress,
    // Recipe state
    recipeList,
    recipeListPage,
    recipeListTotalPages,
    recipeListLoading,
    // Action state
    actionList,
    actionListPage,
    actionListTotalPages,
    actionListLoading,
    // Expanded recipe
    expandedRecipeId,
    expandedRecipe,
    expandedRecipeQueryLoading,
    expandRecipe,
    refreshExpandedRecipe,
    // Loaders
    loadRecipes,
    loadActions,
    loadCurrentView,
    // Mutations
    handleCreateRecipe,
    handleCreateAction,
    handleUpdateAbstractAction,
    handleToggleStatus,
    handleDelete,
    handleLinkAction,
    handleRemoveAction,
    handleAddArticle,
    handleRemoveArticle,
    searchActionsForLink,
    searchArticles,
    fetchActionDetail,
  };
}
